import { useState } from 'react';
import { useConfirm } from '../../components/ui';
import { ApiError, api } from '../../services/api';
import { t } from '../../i18n';
import {
  AutoAllocateParams,
  AutoAllocatePreview,
} from '../../types/payment';

export function selectionKey(paymentId: number, invoiceId: number) {
  return `${paymentId}:${invoiceId}`;
}

export function allSelectionKeys(preview: AutoAllocatePreview): string[] {
  return preview.links.flatMap((link) =>
    link.allocations.map((row) => selectionKey(link.paymentId, row.invoiceId)),
  );
}

export function useAutoAllocate(getParams: () => AutoAllocateParams) {
  const reportLabels = t().reports;
  const confirm = useConfirm();
  const [preview, setPreview] = useState<AutoAllocatePreview | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [replaceExisting, setReplaceExisting] = useState(false);

  const allowRematch = getParams().customerId != null;
  const previewKeys = preview ? allSelectionKeys(preview) : [];
  const allSelected =
    previewKeys.length > 0 && previewKeys.every((key) => selectedKeys.has(key));
  const someSelected = previewKeys.some((key) => selectedKeys.has(key));

  function resolvedParams(
    overrides?: Partial<AutoAllocateParams>,
  ): AutoAllocateParams {
    const base = getParams();
    const rematch =
      base.customerId != null &&
      (overrides?.replaceExisting ?? replaceExisting);
    return {
      ...base,
      ...overrides,
      replaceExisting: rematch,
    };
  }

  async function loadPreview(params: AutoAllocateParams) {
    setError(null);
    setSuccess(null);
    setPreviewing(true);
    try {
      const data = await api.previewPaymentAutoAllocate(params);
      setPreview(data);
      setSelectedKeys(new Set(allSelectionKeys(data)));
      setPreviewOpen(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t().common.error);
    } finally {
      setPreviewing(false);
    }
  }

  async function openPreview() {
    await loadPreview(resolvedParams());
  }

  async function changeReplaceExisting(next: boolean) {
    const previous = replaceExisting;
    setReplaceExisting(next);
    if (!previewOpen) return;

    setError(null);
    setPreviewing(true);
    try {
      const data = await api.previewPaymentAutoAllocate(
        resolvedParams({ replaceExisting: next }),
      );
      setPreview(data);
      setSelectedKeys(new Set(allSelectionKeys(data)));
    } catch (err) {
      setReplaceExisting(previous);
      setError(err instanceof ApiError ? err.message : t().common.error);
    } finally {
      setPreviewing(false);
    }
  }

  function closePreview() {
    if (applying) return;
    setPreviewOpen(false);
  }

  function toggleSelection(key: string) {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  async function applySelected() {
    if (!preview || selectedKeys.size === 0) return false;
    const rematch = allowRematch && replaceExisting;
    const ok = await confirm({
      title: reportLabels.autoAllocateConfirmTitle,
      message: rematch
        ? reportLabels.autoAllocateConfirmRematch
        : reportLabels.autoAllocateConfirmMessage,
      confirmText: `${reportLabels.autoAllocateApply} (${selectedKeys.size})`,
      cancelText: t().common.cancel,
      confirmVariant: rematch ? 'danger' : 'default',
      zIndex: 110,
    });
    if (!ok) return false;

    setError(null);
    setApplying(true);
    try {
      const selections = Array.from(selectedKeys).map((key) => {
        const [paymentId, invoiceId] = key.split(':');
        return {
          paymentId: Number(paymentId),
          invoiceId: Number(invoiceId),
        };
      });
      const result = await api.applyPaymentAutoAllocate({
        ...resolvedParams(),
        selections,
      });
      setPreviewOpen(false);
      setPreview(null);
      setSuccess(
        result.skipped > 0
          ? `${reportLabels.autoAllocateApplied} ${reportLabels.autoAllocateSkipped}: ${result.skipped}.`
          : reportLabels.autoAllocateApplied,
      );
      return true;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t().toasts.saveError);
      return false;
    } finally {
      setApplying(false);
    }
  }

  return {
    preview,
    previewOpen,
    previewing,
    applying,
    selectedKeys,
    setSelectedKeys,
    previewKeys,
    allSelected,
    someSelected,
    error,
    success,
    setError,
    setSuccess,
    allowRematch,
    replaceExisting,
    changeReplaceExisting,
    openPreview,
    closePreview,
    toggleSelection,
    applySelected,
  };
}
